import Trix from "trix";

class TrixConfiguration {
    static originalTrixToolbar = Object.freeze(Trix.config.toolbar.getDefaultHTML);
    static currentElement = null 

    static originalTrixToolbarTemplate ({lang} = Trix.config) {
      const toolbar = document.createElement('template');
      toolbar.innerHTML = TrixConfiguration.originalTrixToolbar(arguments);
      return toolbar;
    }
  
    static makeHeadingButton (level) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.trixAttribute = `heading${level}`;
      button.dataset.trixKey = level;
      button.dataset.headingLevel = level;
      button.classList.add('trix-button');
      button.classList.add(`trix-button-heading-${level}`);
      return button;
    }

    /* Because string localisation is not available until DOMContentLoaded has fired,
      but Trix.config.toolbar.getDefaultHTML must be changed before DOMContentLoaded fires,
      we localise the heading button titles after DOMContentLoaded has fired, using this function. */
    static setHeadingButtonLabels (within = document.body) {
      for (const button of within.querySelectorAll('trix-toolbar [data-heading-level]')) {
        button.title = $.t('Heading {{level}}', {level: parseInt(button.dataset.headingLevel)});
      }
    }
  
    static makeUnderlineButton () {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.trixAttribute = 'underline';
      button.dataset.trixKey = 'u';
      button.classList.add('trix-button');
      button.classList.add('trix-button--icon');
      button.classList.add('trix-button--icon-underline');
      return button;
    }

    static makeTableButton() {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.trixAttribute = 'attach';
      button.dataset.action = "rich-text-table#attachTable"
      button.classList.add('trix-button');
      button.classList.add('trix-button--icon');
      button.classList.add('trix-button--icon-table');
      return button;
    }
  
    static advancedFormattingToolsAreEnabled = isTrue(
      document.head.dataset.enableAdvancedTrixEditorFormatting
    );
    /* These are the advanced formatting tools built in to Trix, that we remove from the toolbar
      if the SYS_ADVANCED_RICH_TEXT_FORMATTING config-setting is not set to Yes. */
    static advancedFormattingTools = Object.freeze(
      [
        {
          group: 'text-tools',
          tools: ['[data-trix-attribute = "href"]']
        },
        {
          group: 'block-tools',
          tools: [
            '[data-trix-attribute = "heading1"]',
            '[data-trix-attribute = "bullet"]',
            '[data-trix-attribute = "number"]',
            '[data-trix-attribute = "quote"]',
            '[data-trix-attribute = "code"]',
            '[data-trix-action = "decreaseNestingLevel"]',
            '[data-trix-action = "increaseNestingLevel"]'
          ]
        },
        {
          group: 'table-tools',
          tools: ['[data-trix-attribute = "attach"]']
        },
      ]
    );
  
    static removeAdvancedFormattingToolsFromToolbar (toolbar) {
      for (const {group, tools} of TrixConfiguration.advancedFormattingTools) {
        const container = toolbar.querySelector(`[data-trix-button-group = "${group}"]`);
  
        if (container == null) {
          continue;
        }
  
        for (const tool of tools) {
          container.querySelector(tool)?.remove();
        }
  
        if (container.childElementCount === 0) {
          container.remove();
        }
      }
  
      return toolbar;
    }
  
    static addSimpleFormattingToolsToToolbar (toolbar) {
      const group = toolbar.querySelector(`[data-trix-button-group = "text-tools"]`);
  
      group.appendChild(TrixConfiguration.makeUnderlineButton());
      return toolbar;
    }
  
    static addExtraAdvancedFormattingToolsToToolbar (toolbar) {
      const group = toolbar.querySelector(`[data-trix-button-group = "block-tools"]`);
  
      toolbar.querySelector('[data-trix-attribute = "heading1"]').remove();
  
      const headings = TrixConfiguration.makeTrixToolbarGroup('headings');
  
      for (let level = 1; level <= 6; ++level) {
        headings.appendChild(TrixConfiguration.makeHeadingButton(level));
      }
      
      // Add attache table button only if data-controller equel rich-text-table
      const currentElement = TrixConfiguration.currentElement
      const dataControllerAttribute = currentElement && 
                                      currentElement.parentElement.getAttribute('data-controller')

      if(dataControllerAttribute === 'rich-text-table'){
        const tableGroup = TrixConfiguration.makeTrixToolbarGroup('table-tools');

        tableGroup.appendChild(TrixConfiguration.makeTableButton())
        group.parentNode.insertBefore(tableGroup, group);
      }

      group.parentNode.insertBefore(headings, group);

      return toolbar;
    }
  
    static makeTrixToolbarGroup (groupName) {
      const group = document.createElement('span');
      group.classList.add('trix-button-group');
      group.classList.add(`trix-button-group--${groupName}`);
      group.dataset.trixButtonGroup = groupName;
      return group;
    }
  
    static makeHeadingAttribute (level) {
      return {
        tagName: `h${level}`,
        terminal: true,
        breakOnReturn: true,
        group: false
      };
    }
    
    static extraBlockAttributes = {
      heading2: TrixConfiguration.makeHeadingAttribute(2),
      heading3: TrixConfiguration.makeHeadingAttribute(3),
      heading4: TrixConfiguration.makeHeadingAttribute(4),
      heading5: TrixConfiguration.makeHeadingAttribute(5),
      heading6: TrixConfiguration.makeHeadingAttribute(6),
    };
  
    static simpleTextAttributes = {
      underline: {
        tagName: 'u',
        inheritable: true
      }
    };
  
    /* As we allow Trix editor toolbars to wrap, we remove any spacers, so that
      they don't interfere with the wrapping. Spacing is instead handled by
      column-gap in the CSS rules for toolbars. */
    static removeSpacersFromToolbar (toolbar) {
      for (const spacer of toolbar.querySelectorAll('.trix-button-group-spacer')) {
        spacer.remove();
      }
  
      return toolbar;
    }
  } 

  // For attach table button
  document.addEventListener("trix-before-initialize", function(event) {
    TrixConfiguration.currentElement = event.target    
  })
      
  //Ignore dropped or pasted files
  document.addEventListener("trix-file-accept", function(event) {
    event.preventDefault()
  })

  Object.assign(Trix.config.textAttributes, TrixConfiguration.simpleTextAttributes);
  
  if (TrixConfiguration.advancedFormattingToolsAreEnabled) {
    Object.assign(Trix.config.blockAttributes, TrixConfiguration.extraBlockAttributes);
  
    Trix.config.toolbar.getDefaultHTML = function ({lang} = Trix.config) {
      const toolbar = TrixConfiguration.originalTrixToolbarTemplate(arguments);
      TrixConfiguration.removeSpacersFromToolbar(toolbar.content);
      TrixConfiguration.addSimpleFormattingToolsToToolbar(toolbar.content);
      TrixConfiguration.addExtraAdvancedFormattingToolsToToolbar(toolbar.content);
  
      return toolbar.innerHTML;
    };
  }
  else {
    Trix.config.toolbar.getDefaultHTML = function ({lang} = Trix.config) {
      const toolbar = TrixConfiguration.originalTrixToolbarTemplate(arguments);
      TrixConfiguration.removeSpacersFromToolbar(toolbar.content);
      TrixConfiguration.addSimpleFormattingToolsToToolbar(toolbar.content);
      TrixConfiguration.removeAdvancedFormattingToolsFromToolbar(toolbar.content);
  
      return toolbar.innerHTML;
    };
  }
  
 export class TrixHelpers {
    static restoreSelectionInEditorAfter (editor, action) {
      const originalSelection = editor.getSelectedRange();
  
      try {
        action(editor);
      }
      finally {
        editor.setSelectedRange(originalSelection);
      }
    }
  
    static setSelectionToEndOfEditor (editor) {
      editor.setSelectedRange(editor.getDocument().getLength() - 1);
    }
  
    static appendHTMLToEditor (editor, html) {
      TrixHelpers.setSelectionToEndOfEditor(editor);
      editor.insertHTML(html);
    }
  
    static undoableManipulationWithEditor (editor, undoEntry, action) {
      editor.recordUndoEntry(...undoEntry);
      action(editor);
    }
  }

$(
  () => {
    TrixConfiguration.setHeadingButtonLabels();
  }
);
